import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideTransloco } from '@jsverse/transloco';
import {
  Cobalt,
  CalloutComponent,
  CobaltAutocompleteModule,
  CobaltButtonModule,
  CobaltCheckboxModule,
  CobaltContentModalModule,
  CobaltDatepickerModule,
  CobaltInputModule,
  CobaltSelectModule,
  CobaltSpinnerModule,
  CobaltTableModule,
  CobaltTooltipModule,
  ContentModalComponent,
  IconComponent,
  ToastComponent,
  provideToastService,
} from '@msi/cobalt-prime';
import { definePreset } from '@primeuix/styled';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import {
  AppSpecificConfigToken,
  IamAuthGrantConfigToken,
  UserAuthenticationModule,
} from '@msi/commandcentral-common-header';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { AppComponent } from './app.component';
import { getAppSpecificIamConfig } from './app.config';
import { AppRoutingModule } from './app.routes';
import { DataSharingComponent } from './data-sharing/data-sharing.component';
import { AddAgencyModalComponent } from './data-sharing/add-agency-component/add-agency-modal.component';
import { AuthInterceptorService } from './interceptors/auth-interceptor.service';
import { DeviceAddressTextPipe } from './pipes/device-address-text.pipe';
import { EmptyTextPipe } from './pipes/empty-text.pipe';
import { PremiseHazardTypeItemComponent } from './premise-hazard-type/premise-hazard-type-item/premise-hazard-type-item.component';
import { PremiseHazardTypeListComponent } from './premise-hazard-type/premise-hazard-type-list/premise-hazard-type-list.component';
import { PremiseHazardTypePageComponent } from './premise-hazard-type/premise-hazard-type-page/premise-hazard-type-page.component';
import { PremiseHazardTypeViewComponent } from './premise-hazard-type/premise-hazard-type-view/premise-hazard-type-view.component';
import { PremiseHazardItemComponent } from './premise-hazards/premise-hazard-item/premise-hazard-item.component';
import { PremiseHazardItemContactsComponent } from './premise-hazards/premise-hazard-item-contacts/premise-hazard-item-contacts.component';
import { PremiseHazardListComponent } from './premise-hazards/premise-hazard-list/premise-hazard-list.component';
import PremiseHazardsPageComponent from './premise-hazards/premise-hazard-page/premise-hazard-page.component';
import { PremiseHazardViewComponent } from './premise-hazards/premise-hazard-view/premise-hazard-view.component';
import { SuggestControlComponent } from './premise-hazards/suggest-control/suggest-control.component';
import { PremiseHazardCommandsRestService } from './services/rest/premise-hazard-command-rest.service';
import { PremiseHazardQueryRestService } from './services/rest/premise-hazard-rest.service';
import { TranslocoHttpLoader } from './transloco-loader';
import { TranslocoRootModule } from './transloco-root.module';
import { AttachmentNameLimitPipe } from './pipes/attachment-name-limit.pipe';
import { EllipsisTooltipDirective } from './shared/ellipsis-tooltip.directive';
import { SearchInputComponent } from './shared/search-input/search-input.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FileSizePipe } from './pipes/file-size.pipe';
import { OrganizationSettingsComponent } from './organization-settings/organization-settings.component';
import { OrganizationConfigurationComponent } from './organization-configuration/organization-configuration.component';
import { UnauthorizedComponent } from './unauthorized-component/unauthorized.component';
import { SortIconComponent } from './sort-icon/sort-icon.component';
import { TabsModule } from 'primeng/tabs';
export function tokenGetter() {
  return localStorage.getItem('access_token');
}
@NgModule({
  declarations: [
    AppComponent,
    PremiseHazardListComponent,
    PremiseHazardItemComponent,
    PremiseHazardItemContactsComponent,
    PremiseHazardViewComponent,
    PremiseHazardsPageComponent,
    PremiseHazardTypeItemComponent,
    PremiseHazardTypeListComponent,
    PremiseHazardTypeViewComponent,
    PremiseHazardTypePageComponent,
    DataSharingComponent,
    OrganizationSettingsComponent,
    OrganizationConfigurationComponent,
    EllipsisTooltipDirective,
    OrganizationConfigurationComponent,
    UnauthorizedComponent,
    SortIconComponent
  ],
  imports: [
    AppRoutingModule,
    // Cobalt Modules
    CalloutComponent,
    CobaltButtonModule,
    CobaltCheckboxModule,
    CobaltContentModalModule,
    CobaltDatepickerModule,
    ContentModalComponent,
    CobaltTableModule,
    IconComponent,
    CobaltInputModule,
    CobaltSelectModule,
    CobaltSpinnerModule,
    CobaltAutocompleteModule,
    ToastComponent,
    CobaltTooltipModule,
    // End Cobalt Modules
    NgxMaskDirective,
    NgxMaskPipe,
    CommonModule,
    BrowserModule,
    EmptyTextPipe,
    AttachmentNameLimitPipe,
    FileSizePipe,
    DeviceAddressTextPipe,
    TranslocoRootModule,
    UserAuthenticationModule,
    ReactiveFormsModule,
    FormsModule,
    AddAgencyModalComponent,
    SearchInputComponent,
    SuggestControlComponent,
    ConfirmDialog,
    TabsModule,
  ],
  providers: [
    provideAnimations(),
    provideToastService(),
    {
      provide: IamAuthGrantConfigToken,
      useFactory: getAppSpecificIamConfig,
    },
    {
      provide: AppSpecificConfigToken,
      useValue: getAppSpecificIamConfig(),
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true,
    },
    provideHttpClient(),
    provideTransloco({
      config: {
        availableLangs: ['en', 'es', 'fr', 'de', 'pt'],
        defaultLang: 'en',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideNgxMask(),
    providePrimeNG({
      theme: {
        preset: definePreset(Cobalt, {
          components: {
            checkbox: {
              colorScheme: {
                light: { root: { checkedBackground: 'transparent', checkedHoverBackground: 'transparent' } },
                dark: { root: { checkedBackground: 'transparent', checkedHoverBackground: 'transparent' } },
              },
            },
          },
        }),
        options: { prefix: 'msi', darkModeSelector: '.dark' },
      },
    }),
    PremiseHazardQueryRestService,
    PremiseHazardCommandsRestService,
    ConfirmationService,
    DialogService,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
